import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Area, AreaChart, CartesianGrid, ResponsiveContainer, Tooltip, XAxis, YAxis } from "recharts";
import { Activity, Eye, Globe2, Monitor, Search, SearchX, Share2, Star, Users, FileText } from "lucide-react";

const RANGES = [7, 30, 90];
const fmtDay = (iso) => new Date(iso + "T00:00:00Z").toLocaleDateString("sq-AL", { day: "numeric", month: "short", timeZone: "UTC" });

const Kpi = ({ icon: Icon, label, value, sub, live, testid }) => (
    <div className="border border-slate-200 bg-white p-4" data-testid={testid}>
        <div className="flex items-center justify-between label-kicker text-slate-500">
            {label}
            {live ? <span className="w-2 h-2 rounded-full bg-emerald-500 adm-pulse" /> : <Icon className="w-4 h-4" />}
        </div>
        <div className="font-display font-black text-3xl mt-2">{value}</div>
        {sub && <div className="text-xs font-mono text-slate-500 mt-1">{sub}</div>}
    </div>
);

const Panel = ({ icon: Icon, title, children, testid }) => (
    <div className="border border-slate-200 bg-white" data-testid={testid}>
        <div className="px-4 py-3 border-b border-slate-200 flex items-center gap-2">
            <Icon className="w-4 h-4 text-slate-500" />
            <div className="font-display font-bold uppercase text-sm">{title}</div>
        </div>
        <div className="p-4">{children}</div>
    </div>
);

const BarList = ({ rows, labelKey = "key", empty = "Ende pa të dhëna", testid }) => {
    const max = Math.max(1, ...rows.map((r) => r.count));
    if (!rows.length) return <div className="text-sm text-slate-500" data-testid={`${testid}-empty`}>{empty}</div>;
    return (
        <ul className="space-y-2" data-testid={testid}>
            {rows.map((r, i) => (
                <li key={`${r.key}-${i}`} className="text-sm">
                    <div className="flex justify-between gap-3 mb-1">
                        <span className="truncate">{r[labelKey] || r.key}</span>
                        <span className="font-mono text-xs text-slate-500">{r.count}</span>
                    </div>
                    <div className="h-1.5 bg-slate-100">
                        <div className="h-full bg-[hsl(var(--primary))] transition-[width] duration-700" style={{ width: `${(r.count / max) * 100}%` }} />
                    </div>
                </li>
            ))}
        </ul>
    );
};

export default function VisitorsTab() {
    const [days, setDays] = useState(7);
    const [d, setD] = useState(null);

    useEffect(() => {
        const load = () => api.get("/admin/visitors", { params: { days } }).then(({ data }) => setD(data)).catch(() => {});
        load();
        const id = setInterval(load, 30000);
        return () => clearInterval(id);
    }, [days]);

    if (!d) return <div className="text-sm text-slate-500" data-testid="visitors-loading">Duke ngarkuar…</div>;

    return (
        <div className="space-y-6" data-testid="admin-visitors-tab">
            <div className="flex flex-wrap items-center justify-between gap-3">
                <div className="text-sm text-slate-500">Vizitorët e faqes (pa llogaritur adminët). Rifreskohet çdo 30 sek.</div>
                <div className="inline-flex border border-slate-200">
                    {RANGES.map((r) => (
                        <button key={r} onClick={() => setDays(r)} data-testid={`visitors-range-${r}`}
                            className={`px-3 py-1.5 text-xs font-mono font-bold uppercase ${days === r ? "bg-slate-900 text-white" : "text-slate-600 hover:bg-slate-100"}`}>
                            {r} ditë
                        </button>
                    ))}
                </div>
            </div>

            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                <Kpi live label="Online tani" value={d.online_now} sub={d.online_pages[0] ? `te ${d.online_pages[0].key}` : "—"} testid="visitors-online-now" />
                <Kpi icon={Users} label="Vizitorë sot" value={d.today.visitors} sub={`${d.today.views} faqe të hapura`} testid="visitors-today" />
                <Kpi icon={Users} label={`Vizitorë · ${d.days} ditë`} value={d.visitors} sub="vizitorë unikë" testid="visitors-period" />
                <Kpi icon={Eye} label="Faqe të hapura" value={d.views} sub={`${d.pages_per_visitor} faqe / vizitor`} testid="visitors-views" />
            </div>

            <Panel icon={Activity} title={`Vizitorë në ditë · ${d.days} ditët e fundit`} testid="visitors-daily-chart">
                <div className="h-56">
                    <ResponsiveContainer width="100%" height="100%">
                        <AreaChart data={d.daily} margin={{ top: 5, right: 10, left: -20, bottom: 0 }}>
                            <defs>
                                <linearGradient id="vgrad" x1="0" y1="0" x2="0" y2="1">
                                    <stop offset="0%" stopColor="hsl(var(--primary))" stopOpacity={0.45} />
                                    <stop offset="100%" stopColor="hsl(var(--primary))" stopOpacity={0} />
                                </linearGradient>
                            </defs>
                            <CartesianGrid strokeDasharray="3 3" stroke="rgba(148,163,184,0.2)" />
                            <XAxis dataKey="date" tickFormatter={fmtDay} tick={{ fontSize: 11 }} />
                            <YAxis allowDecimals={false} tick={{ fontSize: 11 }} />
                            <Tooltip labelFormatter={fmtDay} formatter={(v, n) => [v, n === "visitors" ? "Vizitorë" : "Faqe"]} />
                            <Area type="monotone" dataKey="views" stroke="#64748b" fill="transparent" strokeDasharray="4 3" />
                            <Area type="monotone" dataKey="visitors" stroke="hsl(var(--primary))" strokeWidth={2} fill="url(#vgrad)" />
                        </AreaChart>
                    </ResponsiveContainer>
                </div>
            </Panel>

            <div className="grid lg:grid-cols-2 gap-6">
                <Panel icon={FileText} title="Faqet më të hapura" testid="visitors-top-pages">
                    <BarList rows={d.top_pages} labelKey="label" testid="visitors-top-pages-list" />
                </Panel>
                <Panel icon={Star} title="Produktet më të shikuara" testid="visitors-top-products">
                    <BarList rows={d.top_products.map((p) => ({ ...p, label: [p.part_number, p.name].filter(Boolean).join(" · ") }))} labelKey="label" testid="visitors-top-products-list" />
                </Panel>
                <Panel icon={Search} title="Kërkimet më të shpeshta" testid="visitors-top-searches">
                    <BarList rows={d.top_searches} testid="visitors-top-searches-list" />
                </Panel>
                <Panel icon={SearchX} title="Kërkime pa rezultat" testid="visitors-zero-searches">
                    <BarList rows={d.zero_searches} empty="Asnjë kërkim pa rezultat" testid="visitors-zero-searches-list" />
                </Panel>
                <Panel icon={Share2} title="Nga vijnë vizitorët" testid="visitors-sources">
                    <BarList rows={d.sources} testid="visitors-sources-list" />
                </Panel>
                <Panel icon={Monitor} title="Pajisja" testid="visitors-devices">
                    <BarList rows={d.devices} testid="visitors-devices-list" />
                </Panel>
                <Panel icon={Globe2} title="Shteti" testid="visitors-countries">
                    <BarList rows={d.countries} testid="visitors-countries-list" />
                </Panel>
            </div>
        </div>
    );
}
